import { Capacitor } from '@capacitor/core';
import { DatabaseInUse, type SqlDriver } from './driver';
import { migrate } from './migrations';

/** Picks the platform driver and brings the schema up to date. */
export async function openDatabase(): Promise<SqlDriver> {
  const t0 = performance.now();
  let driver: SqlDriver;
  try {
    driver = Capacitor.isNativePlatform()
      ? await (await import('./native/nativeDriver')).createNativeDriver()
      : await (await import('./web/workerDriver')).createWebDriver();
  } catch (e) {
    // Open in another tab: once it is free here, bring the schema up to date like any open.
    if (e instanceof DatabaseInUse) throw new DatabaseInUse(async () => prepare(await e.whenFree(), performance.now()));
    throw e;
  }
  return prepare(driver, t0);
}

async function prepare(driver: SqlDriver, t0: number): Promise<SqlDriver> {
  const opened = performance.now();
  await migrate(driver);
  if (debugging()) console.info(`[db] open ${Math.round(opened - t0)} ms, migrate ${Math.round(performance.now() - opened)} ms`);
  return debugging() ? timed(driver) : driver;
}

function debugging(): boolean {
  try {
    return localStorage.getItem('budget.debug') === '1';
  } catch {
    return false;
  }
}

/** Debug builds only (budget.debug): every call slower than 30 ms is logged with its time, to find what a screen waits on. */
function timed(db: SqlDriver): SqlDriver {
  const log = (what: string, sql: string, t0: number) => {
    const ms = performance.now() - t0;
    // Packed reads: the inner query says what was asked.
    const inner = sql.includes(') AS j FROM (') ? sql.slice(sql.indexOf(') AS j FROM (') + 13) : sql;
    // During the first seconds every call, with when it started and ended: a start that waits shows as a gap.
    const early = t0 < 6000;
    if (ms > 30 || early) console.info(`[db${ms > 30 ? ' slow' : ''}] ${Math.round(ms)} ms @${Math.round(t0)}→${Math.round(t0 + ms)} ${what} ${inner.replace(/\s+/g, ' ').slice(0, 120)}`);
  };
  return {
    ...db,
    kind: db.kind,
    persistent: db.persistent,
    async run(sql, params) {
      const t0 = performance.now();
      await db.run(sql, params);
      log('run', sql, t0);
    },
    async all(sql, params) {
      const t0 = performance.now();
      const rows = await db.all(sql, params);
      log(`all(${rows.length})`, sql, t0);
      return rows as never;
    },
    async batch(statements) {
      const t0 = performance.now();
      await db.batch(statements);
      log(`batch(${statements.length})`, statements[0]?.sql ?? '', t0);
    },
  };
}
